import {
	isRouteErrorResponse,
	Link,
	useLoaderData,
	useNavigate,
	useRouteError,
} from '@remix-run/react'
import {
	type LoaderFunction,
	type LoaderFunctionArgs,
	type MetaFunction,
	json,
} from '@remix-run/node'
import MetadataProfile from '#app/components/metadataProfile/collectionMetadataProfile'
import Section from '#app/components/layout/section.tsx'
import Button from '#app/components/buttons/button'
import { captureRemixErrorBoundaryError } from '@sentry/remix'
import { TypographyParagraph } from '../components/typography'
import generateCollectionMeta from '#app/utils/meta/collectionMeta.ts'
import { mapNFTsToCollection } from '#app/utils/misc.server.ts'
import {
	getAttestationv2,
	getNFTsForCollection,
} from '#app/server/db.server.ts'
import { mapReferenceEntriesToFormData } from '#app/utils/misc.tsx'
import { AttestationType, CollectionNFTEntry } from '#types/loreTypes.tsx'

export const meta: MetaFunction<typeof loader> = ({
	data,
	params,
	location,
	matches,
}) => {
	if (data) {
		const { origin, collection } = data
		const { pathname } = location
		const metaPayload = generateCollectionMeta({
			origin,
			path: pathname,
			collectionAttestation: collection,
		})
		return metaPayload
	} else {
		const parentMeta = matches.flatMap(match => match.meta ?? [])
		return parentMeta
	}
}

export const loader: LoaderFunction = async ({
	request,
	params,
}: LoaderFunctionArgs) => {
	const url = new URL(request.url)
	let collectionId = params.cid

	if (!collectionId) {
		return json({
			collection: undefined,
			origin: url.origin,
			atomicLoreUrl: url.origin,
			atomicSignUrl: import.meta.env.VITE_ATOMICSIGN_URL,
		})
	}

	let collection: AttestationType | undefined = undefined
	let nfts: CollectionNFTEntry[] = []
	let nftMessage: string = ''
	let nftCount = 0

	try {
		const retrievedcollection = await getAttestationv2({
			attestationIPFSCID: collectionId,
		})
		if (
			retrievedcollection &&
			retrievedcollection.attestations &&
			retrievedcollection.attestations.length
		) {
			collection = retrievedcollection.attestations[0]
		} else {
			console.error('COLLECTION ATTESTATION NOT FOUND', {
				cid: params.cid,
			})
			throw new Response(
				`Error retrieving attestation for collection with cid or slug: ${params.cid}`,
				{
					status: 500,
				},
			)
		}
	} catch (e) {
		console.error('ERROR LOADING ATTESTATION FOR COLLECTION', {
			e,
			cid: params.cid,
		})
		throw new Response(
			`Error retrieving attestation for collection with cid or slug: ${params.cid}`,
			{
				status: 500,
			},
		)
	}

	if (!params.cid) {
		throw new Error('URL has no cid')
	}

	try {
		const nftrr = await getNFTsForCollection({ attestationCID: params.cid })
		if (nftrr) {
			if (nftrr && nftrr.nfts?.length && nftrr.nfts?.length > 0) {
				nfts = nftrr.nfts
			} else {
				nfts = []
			}
			nftMessage = nftrr.message
		}
	} catch (e) {
		console.error('ERROR FETCHING NFTS', e)
		nfts = []
	}
	try {
		if (collection) {
			// @ts-ignore
			const pendingSignNFTProperties =
				collection.references?.entry?.nft &&
				collection.template.slug === 'collection'
					? collection.references.entry.nft
						? mapReferenceEntriesToFormData(collection.references.entry.nft)
						: []
					: []
			if (pendingSignNFTProperties && pendingSignNFTProperties.length > 0) {
				collection.properties.nfts = pendingSignNFTProperties
				const mappedNFTResponse = await mapNFTsToCollection(collection)
				if (mappedNFTResponse.nftCount) nftCount = mappedNFTResponse.nftCount
			}
		}
	} catch (e) {
		console.error('ERROR UPDATING PENDING SIGN NFT PROPERTIES', e)
	}

	return json({
		cid: collection.references.attestationIPFSCID,
		collection,
		nfts,
		nftMessage,
		nftCount: nftCount > nfts.length ? nftCount : nfts.length,
		atomicLoreUrl: url.origin,
		origin: url.origin,
	})
}

export default function metadataPage() {
	const { cid, collection, nfts, nftMessage, nftCount, atomicLoreUrl, origin } =
		useLoaderData<typeof loader>()

	return (
		<MetadataProfile
			cid={cid}
			collection={collection}
			nfts={nfts}
			nftMessage={nftMessage}
			nftCount={nftCount}
			atomicLoreUrl={atomicLoreUrl}
		/>
	)
}

export function ErrorBoundary() {
	const error = useRouteError()
	captureRemixErrorBoundaryError(error)
	const navigate = useNavigate()

	if (isRouteErrorResponse(error)) {
		console.error('Error is:', error)
		return (
			<Section title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An error occurred while loading this Attestation
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Status: {error.status} - {error.statusText}
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Data: {error.data}
				</TypographyParagraph>
				<Link to="/" prefetch="none">
					<Button>Go Home</Button>
				</Link>
				<TypographyParagraph size="medium">or</TypographyParagraph>
				<Button onClick={() => navigate('.', { replace: true })}>
					Reload Page
				</Button>
			</Section>
		)
	} else if (error instanceof Error) {
		console.error('Error is:', error)
		return (
			<Section title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An error occurred while loading this Attestation
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Message: {error.message}
				</TypographyParagraph>
				<Link to="/" prefetch="none">
					<Button>Go Home</Button>
				</Link>
				<TypographyParagraph size="medium">or</TypographyParagraph>
				<Button onClick={() => navigate('.', { replace: true })}>
					Reload Page
				</Button>
			</Section>
		)
	} else {
		return (
			<Section title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An unknown error occurred while loading this Attestation
				</TypographyParagraph>
				<Link to="/" prefetch="none">
					<Button>Go Home</Button>
				</Link>
				<TypographyParagraph size="medium">or</TypographyParagraph>
				<Button onClick={() => navigate('.', { replace: true })}>
					Reload Page
				</Button>
			</Section>
		)
	}
}
